import { Box, Container } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '../../components/footers/Footer';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import { CategoryPageHeader } from './components/CategoryPageHeader';
import { CategorySidebar } from './components/CategorySidebar';
import { CategoryTopicRow } from './components/CategoryTopicRow';
import { FaqAccordion } from './components/FaqAccordion';
import { buildCategoryPage } from './repository';
import { KC_COLORS } from './theme';
import { NotFound } from '../notFound/NotFound';
import './knowledgeCenter.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export function KnowledgeCenterCategory({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.topicSlug;
  const pageScope = useRef(null);
  const category = buildCategoryPage(slug);

  useGSAP(
    () => {
      if (!category) return undefined;

      const intro = gsap.timeline({ defaults: { ease: 'power2.out' } });
      intro
        .fromTo(
          '.kc-category-breadcrumb',
          { y: 16, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.45 }
        )
        .fromTo(
          '.kc-category-title',
          { y: 28, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.55 },
          '-=0.2'
        )
        .fromTo(
          '.kc-category-desc',
          { y: 18, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.45 },
          '-=0.25'
        )
        .fromTo(
          '.kc-category-meta',
          { y: 14, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4 },
          '-=0.2'
        );

      ScrollTrigger.create({
        trigger: '.kc-category-body',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-category-section-title',
            { y: 16, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.45, ease: 'power2.out' }
          );
          gsap.fromTo(
            '.kc-category-topic',
            { y: 20, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.4,
              stagger: 0.06,
              ease: 'power2.out',
              delay: 0.05,
            }
          );
          gsap.fromTo(
            '.kc-category-sidebar',
            { opacity: 0 },
            { opacity: 1, duration: 0.45, ease: 'power2.out', delay: 0.1 }
          );
        },
      });

      return undefined;
    },
    { scope: pageScope, dependencies: [slug] }
  );

  if (!category) {
    return <NotFound />;
  }

  return (
    <>
      <PageSeoHelmet {...category.seo} />
      <div className="kc-ds w-full overflow-x-hidden bg-white">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain ref={pageScope} className="knowledge-center-category-page">
          <Box className="w-full bg-white pb-12 md:pb-16">
            <Container maxWidth="lg">
              <CategoryPageHeader category={category} />

              <hr
                className="kc-category-divider mt-8 md:mt-10 mb-8 md:mb-10 border-0 border-t"
                style={{ borderColor: KC_COLORS.border }}
              />

              <div className="kc-category-body">
                <h2 className="kc-category-section-title kc-type-h2 text-left mb-4 md:mb-5 pl-0">
                  Articles in This Category
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-8 lg:gap-10 items-start">
                  <div className="min-w-0 text-left space-y-0">
                    {category.topics.map((topic) => (
                      <CategoryTopicRow key={topic.id} topic={topic} />
                    ))}
                  </div>

                  <div className="hidden lg:block">
                    <CategorySidebar items={category.sidebarItems} />
                  </div>
                </div>

                {category.faqs?.length ? (
                  <div id="kc-category-faqs" className="mt-10 md:mt-12 scroll-mt-28">
                    <h2 className="kc-category-section-title kc-type-h2 text-left mb-4 md:mb-5 pl-0">
                      Frequently Asked Questions
                    </h2>
                    <FaqAccordion
                      faqs={category.faqs}
                      idPrefix={`kc-cat-faq-${category.slug}`}
                    />
                  </div>
                ) : null}
              </div>
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    </>
  );
}

export function KnowledgeCenterCategoryPage() {
  return <KnowledgeCenterCategory />;
}
